<template>
	<MStateBox :loading="initialLoading" :empty="emptyData" class="notice-wrap">
		<CTabs
			v-if="tabOption.length"
			class="c-tab"
			v-model="activeTab"
			:option="tabOption"
			:ellipsis="false"
			@change="changeActive"
		/>
		<CScrollPro
			class="scroll-wrap"
			ref="scrollRef"
			pullDown
			@pull-down="scrollPullDown"
			pullUp
			:moreData="currentListData.hasMore"
			@pull-up="scrollPullUp"
			contentClassName="act-scroll-box"
		>
			<div class="item-wrap" v-if="currentListData.list.length">
				<CCardNoticePanel
					v-for="(item, idx) in currentListData.list"
					:key="`${activeTab}-${idx}-${item.id || idx}`"
					:item="item"
					:border="idx != currentListData.list.length - 1"
					:isUsAnnouncement="true"
					@click="handleClick(item)"
				></CCardNoticePanel>
			</div>
			<m-visible-tips v-if="!currentListData.emptyShow && !currentListData.hasMore" />
			<div v-if="currentListData.emptyShow" class="empty-box">
				<CFullEmpty />
			</div>
		</CScrollPro>
	</MStateBox>
</template>

<script setup>
import { ref, onMounted, computed, nextTick, getCurrentInstance } from 'vue';
import CTabs from '@/view/enterprise/components/c-tabs.vue';
import CScrollPro from '@/view/enterprise/components/c-scroll-pro.vue';
import CCardNoticePanel from '@rebuild/components/CCardNoticePanel.vue';
import CFullEmpty from '@/view/enterprise/components/c-full-empty.vue';
import MStateBox from '@/view/enterprise_rebuild/components/MStateBox.vue';
import i18n from '@/assets/js/i18n';
import { useModuleData } from '@rebuild/hooks/data/useModuleData';

// 获取当前实例以使用 proxy.$set
const { proxy } = getCurrentInstance();

// 数据层 - 使用 useModuleData
const { us_announcement, us_announcement_type } = useModuleData();
const initialLoading = ref(true);

// 模板引用
const scrollRef = ref(null);

// 响应式数据
const activeTab = ref(0);
const tabOption = ref([]);
const ANNOUNCEMENT_RATE = ref([]);
const listData = ref([
	{
		list: [],
		pagestart: 0,
		hasMore: true,
		type: '',
		emptyShow: false
	}
]);

// 空状态判断 - 使用与 MStateBox 兼容的逻辑
const emptyData = computed(() => {
	// 初始状态下，如果有错误或者明确没有数据，则显示空状态
	if (us_announcement_type.error || (!us_announcement_type.loading && !us_announcement_type.data?.data)) {
		return true;
	}
	// 如果所有数据都是空的，也显示空状态
	return us_announcement.empty && us_announcement_type.empty;
});

// 当前激活的列表数据
const currentListData = computed(() => {
	const data = listData.value[activeTab.value];
	return (
		data || {
			list: [],
			pagestart: 0,
			hasMore: true,
			type: '',
			emptyShow: false
		}
	);
});

// 业务方法
function initTab() {
	if (tabOption.value.length) {
		const newListData = ANNOUNCEMENT_RATE.value.map((item, index) => {
			return {
				list: [],
				pagestart: 0,
				hasMore: true,
				type: item.id,
				emptyShow: false
			};
		});
		// 使用 proxy.$set 更新整个数组
		proxy.$set(listData, 'value', newListData);
	} else {
		// 没有Tab时，就请求全部数据
		const newListData = [
			{
				list: [],
				pagestart: 0,
				hasMore: true,
				type: '',
				emptyShow: false
			}
		];
		// 使用 proxy.$set 更新整个数组
		proxy.$set(listData, 'value', newListData);
	}
}

function changeActive(index) {
	activeTab.value = index;

	// 重新初始化滚动组件
	scrollRef.value && scrollRef.value.init();

	if (currentListData.value.list.length == 0 && currentListData.value.hasMore) {
		loadData(true);
	}
}

async function loadData(reload = false) {
	let target = listData.value[activeTab.value];
	console.log('🚀 ~ loadData ~ target:', listData.value, activeTab.value, !target);

	// 确保 target 存在
	if (!target) {
		console.warn('Target data not found for activeTab:', activeTab.value);
		return;
	}

	// 使用 useModuleData 方式，保持与历史组件相同的参数结构
	await us_announcement.refresh({
		pageStart: target.pagestart,
		pageNum: 20,
		typeList: target.type
	});

	// 保持与历史组件相同的数据处理逻辑
	const data = us_announcement.data?.data || [];
	const extra = us_announcement.data?.extra || { hasMore: false, total: 0 };

	// 数据处理
	const processedData = data.map(item => ({
		...item,
		title: item.desc,
		publishDate: item.date
	}));

	const newData = [...target.list, ...processedData];
	// 创建新的对象来确保响应式更新
	const updatedTarget = {
		...target,
		list: reload ? processedData : newData,
		hasMore: extra.hasMore,
		emptyShow: newData.length == 0
	};

	// 使用 proxy.$set 强制触发响应式更新
	proxy.$set(listData.value, activeTab.value, updatedTarget);
	console.log('listData.value', listData.value, activeTab.value, updatedTarget);

	// 确保响应式更新
	// await nextTick();
}

async function scrollPullDown(done) {
	setPageStart('pullDown');
	await loadData(true);
	done();
}

async function scrollPullUp(done) {
	setPageStart('pullUp');
	await loadData();
	done();
}

function setPageStart(type) {
	let _activeTab = activeTab.value;
	let target = listData.value[_activeTab];

	// 创建新的对象来确保响应式更新
	const updatedTarget = {
		...target,
		pagestart: type == 'pullDown' ? 0 : target.pagestart + 1
	};

	// 使用 proxy.$set 强制触发响应式更新
	proxy.$set(listData.value, _activeTab, updatedTarget);
}

// 点击处理
function handleClick(item) {
	// 这里可以添加点击处理逻辑
	console.log('Clicked item:', item);
}

// 生命周期 - created 的逻辑移到这里
onMounted(async () => {
	// 使用 useModuleData 方式获取公告类型
	await us_announcement_type.refresh();
	if (us_announcement_type.error || !us_announcement_type.data?.data) {
		initialLoading.value = false;
		return; // MStateBox 会处理空状态显示
	}

	const composeTabs = [
		{
			'id': '',
			'name': i18n.t('enterprise.all')
		},
		...(us_announcement_type.data.data.map(item => ({ 'id': item, name: item })) || [])
	];
	tabOption.value = composeTabs.map(v => {
		ANNOUNCEMENT_RATE.value.push(v);
		return v.name;
	});

	initTab();
	await loadData(true);
	initialLoading.value = false;
});
</script>

<style lang="less" scoped>
.notice-wrap {
	height: 100%;
	background: #fff;
	/deep/.module-state-content {
		display: flex;
		flex-direction: column;
	}
	.scroll-wrap {
		flex: 1;
		height: 0;
	}
	.item-wrap {
		padding: 0.14rem 0.14rem 0;
	}
	.empty-box {
		height: calc(100vh - 0.4rem);
	}
}
</style>
